import cssVars from '@figma/fpl-tokens/css-vars';
import { color } from '@/colors';
import { themeColor } from '@/UI/themeColors';
import { Box } from '@/UI/Box/Box';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';

type WorkspaceIconSize = 'xs' | 'small' | 'large';

const SIZE_STYLES: Record<WorkspaceIconSize, { dimension: string; fontSize: string }> = {
  xs: { dimension: '20px', fontSize: '12px' },
  small: { dimension: '24px', fontSize: '14px' },
  large: { dimension: '86px', fontSize: '50px' },
};

/**
 * FPL's `MenuV2.Item` renders its highlight on the row (`<li role="menuitem">`), not on the
 * `lead` slot, so the icon has to react to the row's state instead of its own `:hover`.
 * Mirrors the guards FPL uses in `menu-v2.css.ts`: pointer hover on enabled rows, and
 * `:focus-visible` only for keyboard-driven movement (pointer moves focus the row too).
 */
const MENU_ROW_HIGHLIGHT_SELECTOR = [
  '[role="menuitem"]:not([aria-disabled="true"]):hover &',
  '[role="menuitem"]:not([data-movement-type="pointer"]):not([aria-disabled="true"]):focus-visible &',
].join(', ');

interface WorkspaceIconProps {
  text: string;
  size?: WorkspaceIconSize;
}
export const WorkspaceIcon = ({ text, size = 'small' }: WorkspaceIconProps) => {
  const { dimension, fontSize } = SIZE_STYLES[size];
  const isFplMigrationEnabled = useFplMigrationFlag();

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: dimension,
        width: dimension,
        borderRadius: 1,
        backgroundColor: isFplMigrationEnabled ? cssVars.color.iconBrand : themeColor.white100Secondary,
        ...(isFplMigrationEnabled && {
          [MENU_ROW_HIGHLIGHT_SELECTOR]: {
            backgroundColor: cssVars.color.iconBrandSecondary,
          },
        }),
        color: isFplMigrationEnabled ? cssVars.color.textOnbrand : color.Black100,
        fontSize,
        fontWeight: 500,
      }}
    >
      {text?.[0].toLocaleUpperCase()}
    </Box>
  );
};
